import React, { useState, useEffect } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  Box,
  Autocomplete,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  CircularProgress as MUICircularProgress,
  List,
  ListItem,
  ListItemText,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useInventory } from '../../contexts/InventoryContext';
import { useOrder } from '../../contexts/OrderContext';
import { useLocation } from '../../contexts/LocationContext';
import api from '../../utils/api';

const NewOrderModal = ({ open, onClose, onCreated, mode = 'order' }) => {
  const isQuote = mode === 'quote';
  const { items: inventoryItems, loadInventory } = useInventory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { addOrder } = useOrder();
  const { selectedLocation } = useLocation();
  const locationId = selectedLocation?.id || selectedLocation?._id || '';
  
  // Form state
  const [customer, setCustomer] = useState({
    type: 'individual', // individual, business
    firstName: '',
    lastName: '',
    businessName: '',
    email: '',
    phone: ''
  });
  const [items, setItems] = useState([]);
  const [manualItem, setManualItem] = useState({
    name: '',
    sku: '',
    quantity: 1,
    unitPrice: 0,
    notes: ''
  });
  // Live SKU search state (external products)
  const [skuQuery, setSkuQuery] = useState('');
  const [skuResults, setSkuResults] = useState([]);
  const [skuLoading, setSkuLoading] = useState(false);
  const [skuError, setSkuError] = useState('');
  const [shippingAddress, setShippingAddress] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA'
  });
  const [payment, setPayment] = useState({
    method: 'CASH', // CASH, CARD, CHECK, ACIMA, SYNCHRONY, SNAP, PROGRESSIVE, LAYAWAY, OTHER
    status: 'pending' // pending, paid, partially_paid, failed, refunded
  });
  const [delivery, setDelivery] = useState({
    type: 'pickup', // pickup, delivery, shipping
    notes: ''
  });
  // Order date (pre-populated with today's date)
  const [orderDate, setOrderDate] = useState(() => {
    const d = new Date();
    // format yyyy-MM-dd for input[type="date"]
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  });
  const [notes, setNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  // Per-location pricing options and selections
  const [pricingOptions, setPricingOptions] = useState({ shipping: [], warranties: [], taxRate: 0 });
  const [selectedShippingTitle, setSelectedShippingTitle] = useState('');
  const [selectedWarrantyTitle, setSelectedWarrantyTitle] = useState('');
  // Custom fee state
  const [customFee, setCustomFee] = useState('');
  const [customFeeLabel, setCustomFeeLabel] = useState('');
  
  // Customer live search state
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerSearchLoading, setCustomerSearchLoading] = useState(false);

  // Inventory live search state
  const [inventorySearchInput, setInventorySearchInput] = useState('');
  const [inventoryOptions, setInventoryOptions] = useState([]);
  const [inventorySearchLoading, setInventorySearchLoading] = useState(false);

  // Debounced inventory search
  useEffect(() => {
    if (!open || !locationId) return;
    const q = inventorySearchInput.trim();
    setInventorySearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ locationId, page: '1', limit: '30' });
        if (q) params.append('search', q);
        const res = await api.get(`/inventory?${params.toString()}`);
        if (res.data?.success) {
          setInventoryOptions(res.data.data.items || []);
        }
      } catch (e) {
        // ignore
      } finally {
        setInventorySearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [inventorySearchInput, locationId, open]);

  // Debounced customer search
  useEffect(() => {
    if (!locationId) return;
    const q = customerSearchInput.trim();
    setCustomerSearchLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ locationId, page: 1, limit: 30 });
        if (q) params.append('search', q);
        const res = await api.get(`/customers?${params.toString()}`);
        if (res.data?.success) {
          setCustomerOptions(res.data.data.customers || []);
        }
      } catch (e) {
        // ignore
      } finally {
        setCustomerSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [customerSearchInput, locationId]);

  // Load inventory items when modal opens
  useEffect(() => {
    if (open) {
      loadInventory();
      setCustomerSearchInput('');
      setCustomerOptions([]);
      if (locationId) {
        // Load pricing options for selected location
        api.get(`/locations/${locationId}/pricing`)
          .then(res => {
            const pricing = res?.data?.data?.pricing || { shipping: [], warranties: [], taxRate: 0 };
            setPricingOptions(pricing);
          })
          .catch(err => {
            console.error('Failed to load pricing options', err);
            setPricingOptions({ shipping: [], warranties: [], taxRate: 0 });
          });
      }
      // Reset form when opening
      setCustomer({
        type: 'individual',
        firstName: '',
        lastName: '',
        businessName: '',
        email: '',
        phone: ''
      });
      setItems([]);
      setManualItem({
        name: '',
        sku: '',
        quantity: 1,
        unitPrice: 0,
        notes: ''
      });
      setShippingAddress({
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'USA'
      });
      setPayment({
        method: 'CASH',
        status: 'pending'
      });
      setDelivery({
        type: 'pickup',
        notes: ''
      });
      setNotes('');
      setInternalNotes('');
      setSelectedShippingTitle('');
      setSelectedWarrantyTitle('');
      setCustomFee('');
      setCustomFeeLabel('');
      // Reset order date to today when opening
      const d = new Date();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      setOrderDate(`${d.getFullYear()}-${mm}-${dd}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  
  // Add inventory item to order
  const addInventoryItem = (inventoryItem, quantity = 1) => {
    if (!inventoryItem) return;
    
    const newItem = {
      inventoryId: inventoryItem.id,
      name: inventoryItem.name,
      sku: inventoryItem.sku,
      quantity: quantity,
      unitPrice: inventoryItem.retail || 0,
      totalPrice: (inventoryItem.retail || 0) * quantity,
      notes: ''
    };
    setItems(prev => [...prev, newItem]);
  };

  // Add an external product (e.g., Ashley) directly to items list
  const addExternalProductToItems = (product) => {
    if (!product) return;
    const unit = Number(product.salePrice ?? product.wholesalePrice ?? product.msrpPrice ?? 0) || 0;
    const newItem = {
      inventoryId: null,
      name: product.name || 'Item',
      sku: product.sku || product.externalId || ('EXT-' + Date.now()),
      quantity: 1,
      unitPrice: unit,
      totalPrice: unit,
      notes: ''
    };
    setItems(prev => [...prev, newItem]);
  };

  // Debounced search for SKU field (3+ chars)
  useEffect(() => {
    const q = (manualItem.sku || '').trim();
    setSkuQuery(q);
    setSkuError('');
    if (!locationId) {
      setSkuResults([]);
      return;
    }
    if (!q || q.length < 3) {
      setSkuResults([]);
      return;
    }
    let cancelled = false;
    setSkuLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          locationId,
          provider: 'ashley',
          search: q,
          page: '1',
          limit: '10'
        });
        const res = await api.get(`/integrations/external-products/search?${params.toString()}`);
        if (cancelled) return;
        const data = res?.data?.data || {};
        setSkuResults(Array.isArray(data.products) ? data.products : []);
      } catch (err) {
        if (!cancelled) setSkuError(err?.response?.data?.message || err?.message || 'Search failed');
      } finally {
        if (!cancelled) setSkuLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manualItem.sku, locationId]);
  
  // Add manual item to order
  const addManualItem = () => {
    if (!manualItem.name || manualItem.quantity < 1 || manualItem.unitPrice <= 0) return;
    
    const newItem = {
      inventoryId: null,
      name: manualItem.name,
      sku: manualItem.sku || 'MANUAL-' + Date.now(),
      quantity: manualItem.quantity,
      unitPrice: manualItem.unitPrice,
      totalPrice: manualItem.unitPrice * manualItem.quantity,
      notes: manualItem.notes
    };
    
    setItems([...items, newItem]);
    
    // Reset manual item form
    setManualItem({
      name: '',
      sku: '',
      quantity: 1,
      unitPrice: 0,
      notes: ''
    });
  };
  
  // Remove item from order
  const removeItem = (index) => {
    const newItems = [...items];
    newItems.splice(index, 1);
    setItems(newItems);
  };
  
  // Update item quantity
  const updateItemQuantity = (index, quantity) => {
    if (quantity < 1) return;
    
    const newItems = [...items];
    newItems[index].quantity = quantity;
    newItems[index].totalPrice = newItems[index].unitPrice * quantity;
    setItems(newItems);
  };
  
  // Handle form submission
  const handleSubmit = async () => {
    try {
      // Prepare order data
      const orderData = {
        customer,
        orderType: isQuote ? 'QUOTE' : 'SALE',
        documentKind: isQuote ? 'quote' : 'order',
        quote: Boolean(isQuote),
        items: items.map(item => ({
          inventoryId: item.inventoryId,
          name: item.name,
          sku: item.sku,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          notes: item.notes
        })),
        shippingAddress,
        payment,
        delivery,
        notes,
        internalNotes,
        orderDate,
        pricing: {
          selectedExtras: {
            shipping: (delivery.type !== 'pickup' && selectedShippingTitle) ? [selectedShippingTitle] : [],
            warranties: selectedWarrantyTitle ? [selectedWarrantyTitle] : []
          },
          customFee: customFee ? Number(customFee) : 0,
          customFeeLabel: customFeeLabel || null
        }
      };
      
      // Submit order and notify parent with created order
      const created = await addOrder(orderData);
      if (created && typeof onCreated === 'function') {
        try {
          onCreated(created);
        } catch (_) {
          // no-op if parent callback throws
        }
      }
      
      // Close modal and reset form
      onClose();
    } catch (error) {
      console.error('Error creating order:', error);
    }
  };
  
  // Calculate order totals
  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + item.totalPrice, 0);
    const taxRateFrac = Number(pricingOptions.taxRate || 0) / 100;
    // Extras from selected pricing options (broken down)
    let shippingExtraAmount = 0;
    let warrantyExtraAmount = 0;
    // Delivery/shipping extras: include only when an option is selected and delivery type is not pickup
    if (delivery.type !== 'pickup' && selectedShippingTitle) {
      const opt = pricingOptions.shipping.find(s => s.title === selectedShippingTitle);
      if (opt) shippingExtraAmount += Number(opt.cost || 0);
    }
    if (selectedWarrantyTitle) {
      const opt = pricingOptions.warranties.find(w => w.title === selectedWarrantyTitle);
      if (opt) warrantyExtraAmount += Number(opt.cost || 0);
    }
    const discountAmount = 0; // No discount for now
    const customFeeAmount = Number(customFee || 0);
    const taxableBase =
      subtotal + shippingExtraAmount + warrantyExtraAmount + customFeeAmount - discountAmount;
    const taxAmount = Math.round(taxableBase * taxRateFrac * 100) / 100;
    const totalAmount = taxableBase + taxAmount;
    
    return {
      subtotal,
      taxAmount,
      shippingExtraAmount,
      warrantyExtraAmount,
      customFeeAmount,
      discountAmount,
      totalAmount
    };
  };
  
  const totals = calculateTotals();
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">{isQuote ? 'Create new quote' : 'Create new order'}</Typography>
            {isQuote && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Same details as a sale order; saved as a furniture price quotation. Stock is not reserved.
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* Order Meta - Order Date at top */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Order Date"
              type="date"
              value={orderDate}
              onChange={(e) => setOrderDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {/* Customer Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Customer Information</Typography>
            {/* Existing Customer Selection */}
            <Grid container spacing={2} sx={{ mb: 1 }}>
              <Grid item xs={12}>
                <Autocomplete
                  options={customerOptions}
                  loading={customerSearchLoading}
                  filterOptions={(x) => x}
                  inputValue={customerSearchInput}
                  onInputChange={(event, value) => setCustomerSearchInput(value)}
                  getOptionLabel={(option) => {
                    const name = option.company ? option.company : `${option.firstName || ''} ${option.lastName || ''}`.trim();
                    const email = option.email ? ` <${option.email}>` : '';
                    return `${name}${email}`;
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(event, value) => {
                    if (value) {
                      // Autofill customer and address from CRM customer
                      setCustomer({
                        type: value.company ? 'business' : 'individual',
                        firstName: value.firstName || '',
                        lastName: value.lastName || '',
                        businessName: value.company || '',
                        email: value.email || '',
                        phone: value.phone || ''
                      });
                      setShippingAddress({
                        street: value.address?.street || '',
                        city: value.address?.city || '',
                        state: value.address?.state || '',
                        zipCode: value.address?.zipCode || '',
                        country: value.address?.country || 'USA'
                      });
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Existing Customer"
                      placeholder="Type to search by name, company, or email"
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              {customer.type === 'business' ? (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Business Name"
                    value={customer.businessName}
                    onChange={(e) => setCustomer({...customer, businessName: e.target.value})}
                    fullWidth
                  />
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="First Name"
                      value={customer.firstName}
                      onChange={(e) => setCustomer({...customer, firstName: e.target.value})}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Last Name"
                      value={customer.lastName}
                      onChange={(e) => setCustomer({...customer, lastName: e.target.value})}
                      fullWidth
                    />
                  </Grid>
                </>
              )}
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={customer.email}
                  onChange={(e) => setCustomer({...customer, email: e.target.value})}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone"
                  value={customer.phone}
                  onChange={(e) => setCustomer({...customer, phone: e.target.value})}
                  fullWidth
                />
              </Grid>

              {/* Move Customer Type to end of section */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Customer Type"
                  value={customer.type}
                  onChange={(e) => setCustomer({...customer, type: e.target.value})}
                  fullWidth
                  SelectProps={{
                    native: true
                  }}
                >
                  <option value="individual">Individual</option>
                  <option value="business">Business</option>
                </TextField>
              </Grid>
            </Grid>
          </Grid>
          
          {/* Address Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Address Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Street Address"
                  value={shippingAddress.street}
                  onChange={(e) => setShippingAddress({...shippingAddress, street: e.target.value})}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  label="City"
                  value={shippingAddress.city}
                  onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  label="State"
                  value={shippingAddress.state}
                  onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  label="ZIP Code"
                  value={shippingAddress.zipCode}
                  onChange={(e) => setShippingAddress({...shippingAddress, zipCode: e.target.value})}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Country"
                  value={shippingAddress.country}
                  onChange={(e) => setShippingAddress({...shippingAddress, country: e.target.value})}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
          
          {/* Order Items */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Order Items</Typography>
            
            {/* Inventory Item Selection */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={8}>
                <Autocomplete
                  options={inventoryOptions}
                  loading={inventorySearchLoading}
                  filterOptions={(x) => x}
                  inputValue={inventorySearchInput}
                  onInputChange={(event, value) => setInventorySearchInput(value)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  getOptionLabel={(option) => `${option.name} (${option.sku}) - $${option.retail || 0}`}
                  onChange={(event, value) => {
                    if (value) {
                      addInventoryItem(value);
                      setInventorySearchInput('');
                    }
                  }}
                  renderInput={(params) => (
                    <TextField 
                      {...params} 
                      label="Select Inventory Item" 
                      placeholder="Type to search by name or SKU..."
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    const selectedItem = inventoryOptions[0];
                    if (selectedItem) {
                      addInventoryItem(selectedItem);
                    }
                  }}
                  fullWidth
                >
                  Add Selected Item
                </Button>
              </Grid>
            </Grid>
            
            {/* Manual Item Entry */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={5}>
                <TextField
                  label="Product Name"
                  value={manualItem.name}
                  onChange={(e) => setManualItem({...manualItem, name: e.target.value})}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={3}>
                <TextField
                  label="SKU"
                  value={manualItem.sku}
                  onChange={(e) => setManualItem({...manualItem, sku: e.target.value})}
                  fullWidth
                />
              </Grid>
              {/* Live SKU search results */}
              {skuQuery && skuQuery.length >= 3 && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2">Matches for "{skuQuery}"</Typography>
                      {skuLoading && <MUICircularProgress size={16} />}
                    </Box>
                    {skuError && (
                      <Typography color="error" variant="caption">{skuError}</Typography>
                    )}
                    <List dense>
                      {skuResults.length === 0 && !skuLoading ? (
                        <ListItem>
                          <ListItemText primary="No matches found" />
                        </ListItem>
                      ) : (
                        skuResults.map((p) => (
                          <ListItem key={`${p.catalogId}-${p.externalId}`} secondaryAction={
                            <FormControlLabel
                              control={<Checkbox onChange={(e) => { if (e.target.checked) addExternalProductToItems(p); }} />}
                              label="Add"
                            />
                          }>
                            <ListItemText
                              primary={`${p.name || 'Item'}${p.sku ? ` (SKU: ${p.sku})` : ''}`}
                              secondary={`Price: $${Number(p.salePrice ?? p.wholesalePrice ?? p.msrpPrice ?? 0).toFixed(2)}`}
                            />
                          </ListItem>
                        ))
                      )}
                    </List>
                    <Typography variant="caption" color="text.secondary">
                      Source: Ashley external catalog for this location
                    </Typography>
                  </Paper>
                </Grid>
              )}
              
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Quantity"
                  type="number"
                  value={manualItem.quantity}
                  onChange={(e) => setManualItem({...manualItem, quantity: parseInt(e.target.value) || 1})}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={2}>
                <TextField
                  label="Unit Price"
                  type="number"
                  value={manualItem.unitPrice}
                  onChange={(e) => setManualItem({...manualItem, unitPrice: parseFloat(e.target.value) || 0})}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={8}>
                <TextField
                  label="Notes"
                  value={manualItem.notes}
                  onChange={(e) => setManualItem({...manualItem, notes: e.target.value})}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addManualItem}
                  fullWidth
                >
                  Add Manual Item
                </Button>
              </Grid>
            </Grid>
            
            {/* Added Items List */}
            {items.length > 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Added Items ({items.length})
                  </Typography>
                </Grid>
                
                {items.map((item, index) => (
                  <Grid item xs={12} key={index}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1
                    }}>
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          SKU: {item.sku}
                        </Typography>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <IconButton 
                            size="small" 
                            onClick={() => updateItemQuantity(index, item.quantity - 1)}
                          >
                            <RemoveIcon />
                          </IconButton>
                          <TextField
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateItemQuantity(index, parseInt(e.target.value) || 1)}
                            sx={{ width: 60, mx: 1 }}
                            size="small"
                            InputProps={{ inputProps: { min: 1 } }}
                          />
                          <IconButton 
                            size="small" 
                            onClick={() => updateItemQuantity(index, item.quantity + 1)}
                          >
                            <AddIcon />
                          </IconButton>
                        </Box>
                        
                        <Typography variant="body1">
                          ${Number(item.unitPrice).toFixed(2)} × {item.quantity} = ${Number(item.totalPrice).toFixed(2)}
                        </Typography>
                        
                        <IconButton 
                          size="small" 
                          onClick={() => removeItem(index)}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grid>
                ))}
                
                {/* Order Totals */}
                <Grid item xs={12}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end',
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    mt: 2
                  }}>
                    <Grid container spacing={2} sx={{ maxWidth: 300 }}>
                      <Grid item xs={6}>
                        <Typography variant="body2">Subtotal:</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" align="right">
                          ${Number(totals.subtotal).toFixed(2)}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="body2">Tax:</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" align="right">
                          ${Number(totals.taxAmount).toFixed(2)}
                        </Typography>
                      </Grid>
                      
                      {totals.shippingExtraAmount > 0 && (
                        <>
                          <Grid item xs={6}>
                            <Typography variant="body2">
                              {delivery.type === 'delivery' ? 'Delivery Fee:' : delivery.type === 'shipping' ? 'Shipping Fee:' : 'Fee:'}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" align="right">
                              ${Number(totals.shippingExtraAmount).toFixed(2)}
                            </Typography>
                          </Grid>
                        </>
                      )}
                      {totals.warrantyExtraAmount > 0 && (
                        <>
                          <Grid item xs={6}>
                            <Typography variant="body2">Warranty Extra:</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" align="right">
                              ${Number(totals.warrantyExtraAmount).toFixed(2)}
                            </Typography>
                          </Grid>
                        </>
                      )}
                      {totals.customFeeAmount > 0 && (
                        <>
                          <Grid item xs={6}>
                            <Typography variant="body2">{customFeeLabel || 'Custom Fee'}:</Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" align="right">
                              ${Number(totals.customFeeAmount).toFixed(2)}
                            </Typography>
                          </Grid>
                        </>
                      )}
                      
                      <Grid item xs={6}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          Total:
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }} align="right">
                          ${Number(totals.totalAmount).toFixed(2)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            )}
          </Grid>
          
          {/* Payment and Delivery */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Payment & Delivery</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Payment Method"
                  value={payment.method}
                  onChange={(e) => setPayment({...payment, method: e.target.value})}
                  fullWidth
                  SelectProps={{
                    native: true
                  }}
                >
                  <option value="CASH">Cash</option>
                  <option value="CARD">Credit/Debit Card</option>
                  <option value="CHECK">Check</option>
                  <option value="ACIMA">Acima Financing</option>
                  <option value="SYNCHRONY">Synchrony Financing</option>
                  <option value="SNAP">Snap Finance</option>
                  <option value="PROGRESSIVE">Progressive Leasing</option>
                  <option value="LAYAWAY">Layaway</option>
                  <option value="OTHER">Other</option>
                </TextField>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Payment Status"
                  value={payment.status}
                  onChange={(e) => setPayment({...payment, status: e.target.value})}
                  fullWidth
                  SelectProps={{
                    native: true
                  }}
                >
                  <option value="pending">Pending</option>
                  <option value="paid">Paid</option>
                  <option value="partially_paid">Partially Paid</option>
                  <option value="failed">Failed</option>
                  <option value="refunded">Refunded</option>
                </TextField>
              </Grid>
              
              {/* Initial Payment Amount - shown for layaway or partially paid */}
              {(payment.method === 'LAYAWAY' || payment.status === 'partially_paid') && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Initial Payment Amount"
                    type="number"
                    value={payment.initialPayment || ''}
                    onChange={(e) => setPayment({...payment, initialPayment: e.target.value})}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>
                    }}
                    helperText={`Total: $${totals.totalAmount.toFixed(2)} - Remaining after payment: $${(totals.totalAmount - Number(payment.initialPayment || 0)).toFixed(2)}`}
                  />
                </Grid>
              )}
              
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Delivery Type"
                  value={delivery.type}
                  onChange={(e) => setDelivery({...delivery, type: e.target.value})}
                  fullWidth
                  SelectProps={{
                    native: true
                  }}
                >
                  <option value="pickup">Pickup</option>
                  <option value="delivery">Delivery</option>
                  <option value="shipping">Shipping</option>
                </TextField>
              </Grid>
              
              {/* Delivery/Shipping Extra (from Pricing) */}
              {(delivery.type === 'delivery' || delivery.type === 'shipping') && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label={delivery.type === 'delivery' ? 'Select Delivery Option' : 'Select Shipping Option'}
                    value={selectedShippingTitle}
                    onChange={(e) => setSelectedShippingTitle(e.target.value)}
                    fullWidth
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                  >
                    <option value="">{delivery.type === 'delivery' ? 'No delivery extra' : 'No shipping extra'}</option>
                    {pricingOptions.shipping.map((opt, idx) => (
                      <option key={idx} value={opt.title}>
                        {opt.title} (+${Number(opt.cost).toFixed(2)})
                      </option>
                    ))}
                  </TextField>
                </Grid>
              )}

              {/* Warranty Extra (from Pricing) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Select Warranty"
                  value={selectedWarrantyTitle}
                  onChange={(e) => setSelectedWarrantyTitle(e.target.value)}
                  fullWidth
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                >
                  <option value="">No warranty</option>
                  {pricingOptions.warranties.map((opt, idx) => (
                    <option key={idx} value={opt.title}>
                      {opt.title} (+${Number(opt.cost).toFixed(2)})
                    </option>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Delivery Notes"
                  value={delivery.notes}
                  onChange={(e) => setDelivery({...delivery, notes: e.target.value})}
                  fullWidth
                />
              </Grid>

              {/* Custom Fee */}
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Custom Fee Label"
                  value={customFeeLabel}
                  onChange={(e) => setCustomFeeLabel(e.target.value)}
                  fullWidth
                  placeholder="e.g., Assembly Fee, Setup Fee"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Custom Fee Amount"
                  type="number"
                  value={customFee}
                  onChange={(e) => setCustomFee(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          
          {/* Additional Notes */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Additional Notes</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Order Notes"
                  multiline
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="Internal Notes"
                  multiline
                  rows={2}
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={items.length === 0}
        >
          {isQuote ? 'Create quote' : 'Create order'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewOrderModal;
